import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService, ApiResponse } from './base.service';

// ==================== INTERFACES ====================

export interface Schedule {
  id: number;
  position_id: number;
  morning_shifts: number;
  afternoon_shifts: number;
  night_shifts: number;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  position?: SchedulePosition;
}

export interface SchedulePosition {
  id: number;
  name: string;
  company_id: number;
  status: 'active' | 'inactive';
  company?: {
    id: number;
    name: string;
  };
}

/**
 * DTO para criar horário
 * ⚠️ company_id é OBRIGATÓRIO - o posto deve pertencer à empresa
 */
export interface CreateScheduleDto {
  company_id: number; // ✅ OBRIGATÓRIO - ID da empresa
  position_id: number; // ✅ OBRIGATÓRIO - ID do posto (deve pertencer à empresa)
  morning_shifts: number; // ✅ OBRIGATÓRIO - Turnos da manhã
  afternoon_shifts: number; // ✅ OBRIGATÓRIO - Turnos da tarde
  night_shifts: number; // ✅ OBRIGATÓRIO - Turnos da noite
  created_by?: string; // Opcional - Quem criou
  updated_by?: string; // Opcional - Quem atualizou
}

export interface UpdateScheduleDto {
  position_id?: number;
  morning_shifts?: number;
  afternoon_shifts?: number;
  night_shifts?: number;
  updated_by?: string;
}

export interface ScheduleFilters {
  company_id?: number;
  position_id?: number;
  status?: 'active' | 'inactive';
  page?: number;
  limit?: number;
}

export interface ScheduleListResponse {
  data: Schedule[];
  meta: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export interface ScheduleStatistics {
  total_schedules: number;
  total_morning_shifts: number;
  total_afternoon_shifts: number;
  total_night_shifts: number;
  total_shifts: number;
  average_shifts_per_guard: string;
}

/**
 * Posto disponível para seleção no dropdown
 * Usado em: GET /schedules/company/:companyId/positions
 */
export interface AvailablePosition {
  id: number;
  name: string;
  status: 'active' | 'inactive';
  has_schedule: boolean;
}

export interface AvailablePositionsResponse {
  data: AvailablePosition[];
  total: number;
}

// @deprecated - Use AvailablePosition instead
export type AvailableGuard = AvailablePosition;
export type AvailableGuardsResponse = AvailablePositionsResponse;

// ==================== SERVICE ====================

@Injectable({
  providedIn: 'root',
})
export class SchedulesService extends BaseService<Schedule> {
  protected override endpoint = 'schedules';

  constructor(http: HttpClient) {
    super(http);
  }

  // ==================== CREATE ====================

  /**
   * POST /schedules
   * Criar novo horário
   *
   * ⚠️ Validações:
   * - company_id é obrigatório
   * - position_id é obrigatório
   * - O posto deve existir (404 se não existir)
   * - O posto deve pertencer à empresa (400 se não pertencer)
   */
  createSchedule(data: CreateScheduleDto): Observable<ApiResponse<Schedule>> {
    return this.http.post<ApiResponse<Schedule>>(`${this.baseUrl}/${this.endpoint}`, data);
  }

  // ==================== READ ====================

  /**
   * GET /schedules?company_id=1&position_id=2&status=active&page=1&limit=10
   * Listar horários com filtros e paginação
   */
  getSchedules(filters?: ScheduleFilters): Observable<ScheduleListResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.company_id) params = params.set('company_id', filters.company_id.toString());
      if (filters.position_id) params = params.set('position_id', filters.position_id.toString());
      if (filters.status) params = params.set('status', filters.status);
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
    }

    return this.http.get<ScheduleListResponse>(`${this.baseUrl}/${this.endpoint}`, { params });
  }

  /**
   * GET /schedules/company/:companyId?page=1&limit=10
   * Listar horários por empresa
   */
  getSchedulesByCompany(companyId: number, page = 1, limit = 10): Observable<ScheduleListResponse> {
    const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    return this.http.get<ScheduleListResponse>(
      `${this.baseUrl}/${this.endpoint}/company/${companyId}`,
      { params },
    );
  }

  /**
   * GET /schedules/company/:companyId/positions
   * Listar postos disponíveis para seleção no dropdown
   *
   * Retorna todos os postos da empresa com indicação se já tem horário configurado
   */
  getPositionsByCompany(companyId: number): Observable<AvailablePositionsResponse> {
    return this.http.get<AvailablePositionsResponse>(
      `${this.baseUrl}/${this.endpoint}/company/${companyId}/positions`,
    );
  }

  /**
   * @deprecated Use getPositionsByCompany instead
   */
  getGuardsByCompany(companyId: number): Observable<AvailableGuardsResponse> {
    return this.getPositionsByCompany(companyId);
  }

  /**
   * GET /schedules/:id
   * Buscar horário por ID
   */
  getScheduleById(id: number | string): Observable<ApiResponse<Schedule>> {
    return this.http.get<ApiResponse<Schedule>>(`${this.baseUrl}/${this.endpoint}/${id}`);
  }

  /**
   * GET /schedules/position/:positionId
   * Buscar horário por posto
   */
  getScheduleByPosition(positionId: number | string): Observable<ApiResponse<Schedule>> {
    return this.http.get<ApiResponse<Schedule>>(
      `${this.baseUrl}/${this.endpoint}/position/${positionId}`,
    );
  }

  /**
   * @deprecated Use getScheduleByPosition instead
   * GET /schedules/position/:positionId
   */
  getScheduleByGuard(guardId: number | string): Observable<ApiResponse<Schedule>> {
    return this.getScheduleByPosition(guardId);
  }

  // ==================== STATISTICS ====================

  /**
   * GET /schedules/statistics?company_id=1
   * Estatísticas gerais (opcionalmente filtradas por empresa)
   */
  getStatistics(companyId?: number): Observable<ApiResponse<ScheduleStatistics>> {
    let params = new HttpParams();
    if (companyId) {
      params = params.set('company_id', companyId.toString());
    }

    return this.http.get<ApiResponse<ScheduleStatistics>>(
      `${this.baseUrl}/${this.endpoint}/statistics`,
      { params },
    );
  }

  /**
   * GET /schedules/statistics/company/:companyId
   * Estatísticas por empresa (rota específica)
   */
  getStatisticsByCompany(companyId: number): Observable<ApiResponse<ScheduleStatistics>> {
    return this.http.get<ApiResponse<ScheduleStatistics>>(
      `${this.baseUrl}/${this.endpoint}/statistics/company/${companyId}`,
    );
  }

  // ==================== UPDATE ====================

  /**
   * PATCH /schedules/:id
   * Atualizar horário
   */
  updateSchedule(id: number | string, data: UpdateScheduleDto): Observable<ApiResponse<Schedule>> {
    return this.http.patch<ApiResponse<Schedule>>(`${this.baseUrl}/${this.endpoint}/${id}`, data);
  }

  // ==================== DELETE ====================

  /**
   * DELETE /schedules/:id
   * Deletar horário
   */
  deleteSchedule(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${this.endpoint}/${id}`);
  }

  // ==================== LEGACY METHODS (Backward Compatibility) ====================

  /**
   * @deprecated Use createSchedule instead
   */
  createOrUpdate(data: CreateScheduleDto): Observable<ApiResponse<Schedule>> {
    return this.createSchedule(data);
  }

  /**
   * @deprecated Use getScheduleByPosition instead
   */
  getByPositionId(positionId: string): Observable<ApiResponse<Schedule>> {
    return this.getScheduleByPosition(positionId);
  }
}
